import styled from '@emotion/styled';
import {Container, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {H1, Subline} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';
import {useNewsIntroBlock} from 'UI/modules/Blocks/NewsIntroBlock/useNewsIntroBlock';

import type {PageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';
import type {NewsBlockType, NewsIntroBlockType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `NewsBlock` component.
 * This interface specifies all the required and optional properties needed to configure a news block display.
 * It includes configuration for auto-filling articles, manually selected articles, block identification, and introductory content.
 */
interface ComponentProps {
    /**
     * The unique identifier for this news block instance.
     * This ID is used for block intro linking and component identification within the page structure.
     */
    id: NewsBlockType['id'];
    /**
     * The introductory content configuration for the news block.
     * Contains the title, description, and other intro-related settings that appear above the news slider.
     */
    intro: NewsIntroBlockType['intro'];
    /**
     * Optional metadata associated with the page content where this news intro block is displayed.
     * This metadata typically contains information such as publication date, author details, and other page-level information that may be used for display purposes.
     * When provided, this data can be used to show additional context like formatted publication times or author information within the news intro block.
     */
    metaData?: PageData['pageMetaData'];
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     *
     * @default 'NewsBlock'
     */
    testId?: string;
}

/**
 * The `NewsBlock` component renders a block of news articles in a slider format with an optional intro section.
 * It displays news articles either from manually selected articles or auto-filled articles based on the configuration.
 * The component uses a responsive slider to showcase news cards with different layouts for various screen sizes.
 *
 * @param props                  The component props.
 * @param props.autoFill         Determines whether to automatically fill news articles or use manually selected ones.
 * @param props.id               The unique identifier for this news block, used for block intro linking.
 * @param props.intro            The introductory content configuration for the block including title and description.
 * @param props.newsArticles     Manually selected news articles to display in the block.
 * @param props.newsArticlesAuto Automatically selected news articles based on criteria when autoFill is enabled.
 * @param props.testId           A unique identifier for testing purposes to locate this component during automated tests.
 * @returns A React element representing the news block with slider containing news cards.
 *
 * @example
 * ```tsx
 * <NewsBlock
 *     autoFill={false}
 *     id="news-block-1"
 *     intro={{ title: "Latest News", description: "Stay updated with our news" }}
 *     newsArticles={[article1, article2, article3]}
 *     newsArticlesAuto={[]}
 *     testId="latest-news-block"
 * />
 * ```
 */
const NewsIntroBlock = observer(({
    id,
    intro,
    metaData,
    testId = 'NewsBlock'
}: ComponentProps) => {
    const {formattedTime, time} = useNewsIntroBlock(metaData);

    return (
        <Container>
            <ViewportTrigger baseComponent={Header} data-cy={testId}>
                {intro.headline && <MotionH1 id={id ?? ''} variants={FadeInAnimation}>{intro.headline}</MotionH1>}
                {formattedTime && (
                    <>
                        <Spacer y={2} />
                        <MotionSubline dateTime={time} variants={FadeInAnimation}>{formattedTime}</MotionSubline>
                    </>
                )}
                {intro.image && (
                    <>
                        <Spacer y={20} />
                        <MotionImageWrapper variants={FadeInAnimation}>
                            <PayloadImage
                                fetchpriority="high"
                                image={intro.image}
                                isLazy={false}
                            />
                        </MotionImageWrapper>
                    </>
                )}
            </ViewportTrigger>
        </Container>
    );
});

NewsIntroBlock.displayName = 'NewsIntroBlock';

export {NewsIntroBlock};

const Header = styled.header``;

const MotionH1 = H1.withComponent(motion.h1);

const MotionSubline = Subline.withComponent(motion.time);

const MotionImageWrapper = styled(motion.div)`
    background: ${({theme}) => theme.colors.backgroundLight};
    border-radius: 24px;
    max-width: 100rem;
    padding: ${spacing(17)};
    width: fit-content;
`;